/**
 * Turn folds (ticket 3525, upstream pin e3e7cc3f, MIT), ported from upstream
 * `MessagesTimeline.logic.ts`. Settled turns fold their work behind a
 * "Worked for ..." row; subagent spawn rows, question answers, and compaction
 * markers stay visible.
 *
 * @module MessagesTimeline.turnFolds
 */

import type { TurnId } from "@cmmd-forge/contracts";
import {
  formatDuration,
  workEntryDisplayIndicatesToolFailure,
  type TimelineEntry,
} from "../../session-logic";
import { messageEndTime, type TimelineLatestTurn } from "./MessagesTimeline.rowTypes";

function computeElapsedMs(startIso: string, endIso: string): number | null {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.max(0, end - start);
}

function maxIsoTimestamp(a: string | null, b: string | null): string | null {
  if (a === null) return b;
  if (b === null) return a;
  const aMs = Date.parse(a);
  const bMs = Date.parse(b);
  if (!Number.isFinite(aMs)) return b;
  if (!Number.isFinite(bMs)) return a;
  return bMs > aMs ? b : a;
}

interface TurnFold {
  turnId: TurnId;
  anchorEntryId: string;
  createdAt: string;
  hiddenEntryIds: ReadonlySet<string>;
  label: string;
}

/**
 * The session's running turn is authoritative when latestTurn briefly lags or
 * regresses behind it. Otherwise, the latest turn counts as unsettled while it
 * is still running (or has not recorded a completion). This is deliberately
 * keyed on turn lifecycle rather than transient working state: right after the
 * user sends a message, the previous turn is still the "active" one until the
 * server creates the new turn, and folding must not flicker through that window.
 */
export function deriveUnsettledTurnId(
  latestTurn: TimelineLatestTurn | null,
  runningTurnId: TurnId | null,
): TurnId | null {
  if (runningTurnId !== null) {
    return runningTurnId;
  }
  if (!latestTurn) {
    return null;
  }
  const isSettled = latestTurn.completedAt !== null && latestTurn.state !== "running";
  return isSettled ? null : latestTurn.turnId;
}

export function lastUserMessageIndex(timelineEntries: ReadonlyArray<TimelineEntry>): number {
  return timelineEntries.findLastIndex(
    (entry) => entry.kind === "message" && entry.message.role === "user",
  );
}

export function timelineEntryTurnId(entry: TimelineEntry): TurnId | null {
  if (entry.kind === "message") {
    return entry.message.role === "assistant" || entry.message.role === "reasoning"
      ? (entry.message.turnId ?? null)
      : null;
  }
  if (entry.kind === "proposed-plan") {
    return entry.proposedPlan.turnId;
  }
  return entry.kind === "work" ? (entry.entry.turnId ?? null) : null;
}

/**
 * A promptless provider restart replaces the native turn without adding a
 * user message. Keep every provider turn since the latest user message in one
 * visual response until the replacement turn settles. A steer has its own
 * user message, so it naturally starts a new visual response.
 */
export function deriveActiveVisualResponseTurnIds(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  unsettledTurnId: TurnId | null;
  isWorking: boolean;
}): ReadonlySet<TurnId> {
  const turnIds = new Set<TurnId>();
  if (input.unsettledTurnId === null) {
    return turnIds;
  }

  turnIds.add(input.unsettledTurnId);
  if (!input.isWorking) {
    return turnIds;
  }

  const latestUserMessageIndex = lastUserMessageIndex(input.timelineEntries);
  for (let index = latestUserMessageIndex + 1; index < input.timelineEntries.length; index += 1) {
    const turnId = timelineEntryTurnId(input.timelineEntries[index]!);
    if (turnId !== null) {
      turnIds.add(turnId);
    }
  }
  return turnIds;
}

function entryEnd(entry: TimelineEntry): string | null {
  if (entry.kind === "message") return messageEndTime(entry.message) ?? null;
  return entry.createdAt;
}

/**
 * Settled turns fold activity before their terminal assistant message behind
 * a "Worked for ..." row. A single ordinary activity after that message joins
 * the fold, while larger groups and failures stay visible as a trailing summary.
 */
// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function deriveTurnFolds(input: {
  timelineEntries: ReadonlyArray<TimelineEntry>;
  terminalAssistantMessageIds: ReadonlySet<string>;
  latestTurn: TimelineLatestTurn | null;
  unfoldedTurnIds: ReadonlySet<TurnId>;
}): ReadonlyMap<string, TurnFold> {
  interface TurnGroup {
    entries: Array<TimelineEntry>;
    terminalEntry: Extract<TimelineEntry, { kind: "message" }> | null;
    hasStreamingMessage: boolean;
    /**
     * The user message that kicked the turn off. Entry timestamps alone
     * undercount the duration (the first entry appears only once the
     * provider starts producing output), and a turn cut short by a steer may
     * hold a single instantaneous commentary message.
     */
    startBoundary: string | null;
  }
  const groupsByTurnId = new Map<TurnId, TurnGroup>();

  let pendingUserBoundary: string | null = null;
  for (const entry of input.timelineEntries) {
    if (entry.kind === "message" && entry.message.role === "user") {
      pendingUserBoundary = entry.message.createdAt;
      continue;
    }
    // Thinking is work, so it folds with the rest of it. A provider that
    // interleaves a block with every tool call would otherwise leave dozens of
    // "Thought" rows standing beside the "Worked for ..." summary.
    // Nothing folds while the turn is live, which is when traces are watched.
    const turnId = timelineEntryTurnId(entry);
    if (!turnId) {
      continue;
    }
    let group = groupsByTurnId.get(turnId);
    if (!group) {
      group = {
        entries: [],
        terminalEntry: null,
        hasStreamingMessage: false,
        // Each user boundary starts at most one turn; a second turn after the
        // same user message (e.g. a steer-superseded continuation) falls back
        // to its own first entry.
        startBoundary: pendingUserBoundary,
      };
      pendingUserBoundary = null;
      groupsByTurnId.set(turnId, group);
    }
    group.entries.push(entry);
    if (entry.kind === "message") {
      if (input.terminalAssistantMessageIds.has(entry.message.id)) {
        group.terminalEntry = entry;
      }
      // A live turn is already excluded above, so only an answer still being
      // written may hold a fold open. A thinking block stranded by a crashed
      // provider keeps its streaming flag forever and must not.
      if (entry.message.streaming && entry.message.role !== "reasoning") {
        group.hasStreamingMessage = true;
      }
    }
  }

  const foldsByAnchorEntryId = new Map<string, TurnFold>();
  for (const [turnId, group] of groupsByTurnId) {
    if (input.unfoldedTurnIds.has(turnId)) {
      continue;
    }
    if (group.hasStreamingMessage) {
      continue;
    }
    const hiddenEntryIds = new Set<string>();
    const terminalEntryIndex = group.terminalEntry
      ? group.entries.findIndex((entry) => entry.id === group.terminalEntry?.id)
      : group.entries.length;
    // Thinking blocks do not count toward "one trailing activity": a block can
    // follow the answer, and it must not stop that lone tool call from folding
    // the way it did before traces existed. Loop-invariant, so it is counted
    // once: a long turn re-derives these rows on every work-log change.
    const trailingEntryCount = group.entries.filter(
      (candidate, candidateIndex) =>
        candidateIndex > terminalEntryIndex &&
        !(candidate.kind === "message" && candidate.message.role === "reasoning"),
    ).length;
    for (const [index, entry] of group.entries.entries()) {
      if (entry.id === group.terminalEntry?.id) {
        continue;
      }
      const isCompaction =
        entry.kind === "work" && entry.entry.sourceActivityKind === "context-compaction";
      const isSingleTrailingActivity =
        trailingEntryCount === 1 &&
        entry.kind === "work" &&
        !workEntryDisplayIndicatesToolFailure(entry.entry);
      // A thinking block after the answer folds with its turn rather than
      // trailing under it, which is what mobile already does.
      const isReasoning = entry.kind === "message" && entry.message.role === "reasoning";
      if (
        !isCompaction &&
        !isReasoning &&
        index > terminalEntryIndex &&
        !isSingleTrailingActivity
      ) {
        continue;
      }
      // User input, subagent batches, and brokered GitHub blockers stay
      // visible after their turn settles: blockers are actionable asks, and
      // hiding one behind "Worked for ..." buries an approval.
      if (
        entry.kind === "work" &&
        (entry.entry.questionAnswer !== undefined ||
          entry.entry.agentSpawn !== undefined ||
          entry.entry.githubConfirmation !== undefined ||
          entry.entry.githubActionRequired !== undefined)
      ) {
        continue;
      }
      hiddenEntryIds.add(entry.id);
    }
    if (hiddenEntryIds.size === 0) {
      continue;
    }
    // A lone compaction row stays visible on its own; it only folds away as
    // part of a turn that already folds other work. Thinking is the same: a
    // question answered by thought alone keeps its "Thought" row
    // rather than collapsing behind a "Worked for ..." that hides nothing else.
    const hidesFoldableWork = group.entries.some(
      (entry) =>
        hiddenEntryIds.has(entry.id) &&
        !(entry.kind === "work" && entry.entry.sourceActivityKind === "context-compaction") &&
        !(entry.kind === "message" && entry.message.role === "reasoning"),
    );
    if (!hidesFoldableWork) {
      continue;
    }

    const firstEntry = group.entries[0];
    const firstHiddenEntry = group.entries.find((entry) => hiddenEntryIds.has(entry.id));
    const lastEntry = group.entries.at(-1);
    if (!firstEntry || !firstHiddenEntry || !lastEntry) {
      continue;
    }

    const isLatestInterruptedTurn =
      input.latestTurn?.turnId === turnId && input.latestTurn.state === "interrupted";
    // A turn cut short by a steer leaves trailing work entries behind its
    // terminal message — take whichever ended last.
    const lastEntryEnd = entryEnd(lastEntry) ?? lastEntry.createdAt;
    const elapsedMs =
      input.latestTurn?.turnId === turnId &&
      input.latestTurn.startedAt &&
      input.latestTurn.completedAt
        ? computeElapsedMs(input.latestTurn.startedAt, input.latestTurn.completedAt)
        : computeElapsedMs(
            group.startBoundary ?? firstEntry.createdAt,
            maxIsoTimestamp(
              group.terminalEntry ? (messageEndTime(group.terminalEntry.message) ?? null) : null,
              lastEntryEnd,
            ) ?? lastEntryEnd,
          );
    const duration = elapsedMs !== null ? formatDuration(elapsedMs) : null;
    const label = isLatestInterruptedTurn
      ? duration
        ? `You stopped after ${duration}`
        : "You stopped this response"
      : duration
        ? `Worked for ${duration}`
        : "Worked";

    foldsByAnchorEntryId.set(firstHiddenEntry.id, {
      turnId,
      anchorEntryId: firstHiddenEntry.id,
      createdAt: firstHiddenEntry.createdAt,
      hiddenEntryIds,
      label,
    });
  }
  return foldsByAnchorEntryId;
}
